import React, {
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react'

import { ProductCarouselContext } from './context'
import { ProductCarouselItem } from './Item'
import { ProductCarouselList } from './List'

interface ProductCarouselProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'className'> {
  /** Must be a valid CSS aspect ratio value, e.g. `"16/9"` or `1.5` */
  aspectRatio?: number | string;
  /** Array of badge texts to display */
  badges?: string[];
  children: React.ReactNode;
}

export function ProductCarousel ({ aspectRatio = 1, badges, children, ...props }: ProductCarouselProps) {
  const [activeItem, setActiveItem] = React.useState<string | null>(null)
  const frameRef = useRef<HTMLDivElement>(null)
  const portalId = useId()
  const contextValue = useMemo(
    () => ({ activeItem, portalId, setActiveItem }),
    [activeItem, portalId]
  )

  /**
   * Uses an `IntersectionObserver` to detect when a carousel item is scrolled
   * into view and set the active item.
   */
  useLayoutEffect(() => {
    const frame = frameRef.current

    if (!frame) return

    const items = frame.querySelectorAll('[data-carousel-item]')
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveItem(entry.target.id)
          }
        })
      },
      { root: frame, threshold: 0.5 }
    )

    items.forEach((item) => observer.observe(item))

    return () => {
      observer.disconnect()
    }
  }, [children])

  return (
    <section
      {...props}
      aria-label='Image carousel'
      aria-roledescription='carousel'
      className='flex flex-col gap-4 relative'
    >
      <ProductCarouselContext.Provider value={contextValue}>
        <div
          aria-live='polite'
          className='rounded-lg overflow-x-auto snap-x snap-mandatory bg-gray-800 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] outline outline-2 outline-offset-2 outline-transparent focus-visible:outline-thatch-500'
          ref={frameRef}
          style={{
            aspectRatio,
          }}
        >
          <div className='flex h-full'>{children}</div>
        </div>
        <ProductCarouselList />
        {badges && badges.length > 0 && (
          <div className='absolute top-4 left-4 flex gap-2'>
            {badges.map((badge, index) => (
              <span key={index} className='inline-block rounded-lg py-1 px-2 bg-white border-emphasis text-violet-500 body-bold'>
                {badge}
              </span>
            ))}
          </div>
        )}
      </ProductCarouselContext.Provider>
    </section>
  )
}

ProductCarousel.Item = ProductCarouselItem
